import type { ComponentPropsWithoutRef, Ref } from 'react';
import { forwardRef, useMemo } from 'react';
import mergeRefs from 'merge-refs';
import { Combobox, ComboboxItem, ComboboxPopover, useComboboxStore } from '@ariakit/react';

import Input, { type InputProps } from './Input';
import SuggestionsList, {
    SUGGESTIONS_POPOVER_BASE_CLASSNAME,
    SUGGESTIONS_ITEM_BASE_CLASSNAME,
} from './SuggestionsList';
import classNames from '../../utils/classNames';

export type AutocompleteSuggestion = {
    id: string;
    label: string;
    value: string;
    secondaryLabel?: string;
};

export interface AutocompleteInputProps
    extends Omit<InputProps, 'value' | 'defaultValue' | 'onChange'> {
    value: string;
    onValueChange?: (value: string) => void;
    onChange?: InputProps['onChange'];
    suggestions: AutocompleteSuggestion[];
    isLoading?: boolean;
    onSelectSuggestion?: (suggestion: AutocompleteSuggestion) => void;
    emptyState?: React.ReactNode;
}

const AutocompleteInput = forwardRef<HTMLInputElement, AutocompleteInputProps>(
    (
        {
            value,
            onValueChange,
            onChange,
            suggestions,
            isLoading = false,
            onSelectSuggestion,
            className,
            disabled,
            readOnly,
            emptyState,
            ...props
        },
        ref
    ) => {
        const {
            onBlur: inputOnBlur,
            onFocus: inputOnFocus,
            onKeyDown: inputOnKeyDown,
            ...inputProps
        } = props;
        const suggestionsByValue = useMemo(
            () => new Map(suggestions.map((item) => [item.value, item])),
            [suggestions]
        );

        const combobox = useComboboxStore({
            value,
            setValue: (nextValue) => {
                onValueChange?.(nextValue);
            },
            setSelectedValue: (selectedValue) => {
                if (!selectedValue) return;
                const values = Array.isArray(selectedValue) ? selectedValue : [selectedValue];
                values.forEach((selected) => {
                    const suggestion = suggestionsByValue.get(String(selected));
                    if (suggestion) {
                        onSelectSuggestion?.(suggestion);
                    }
                });
            },
        });

        const inputValue = combobox.useState('value');
        const isOpen = combobox.useState('open');
        const activeValue = combobox.useState('activeValue');
        const hasQuery = inputValue.trim().length > 0;
        const showEmptyState = hasQuery && !isLoading && suggestions.length === 0;
        const shouldShowPopover =
            isOpen &&
            hasQuery &&
            !disabled &&
            !readOnly &&
            (isLoading || suggestions.length > 0 || showEmptyState);

        return (
            <div className="relative w-full">
                <Combobox
                    store={combobox}
                    render={(
                        comboboxProps: ComponentPropsWithoutRef<'input'> & {
                            ref?: Ref<HTMLInputElement>;
                        }
                    ) => {
                        const {
                            ref: comboboxRef,
                            onChange: comboboxOnChange,
                            onBlur: comboboxOnBlur,
                            onFocus: comboboxOnFocus,
                            onKeyDown: comboboxOnKeyDown,
                            // Ariakit passes the native HTML `size` prop (number).
                            // Our Input uses `size` for visual variants.
                            // Strip the HTML prop to avoid the type clash.
                            size: _comboboxSize,
                            autoComplete: comboboxAutoComplete,
                            ...comboboxRest
                        } = comboboxProps;

                        return (
                            <Input
                                {...inputProps}
                                {...comboboxRest}
                                autoComplete={inputProps.autoComplete ?? comboboxAutoComplete}
                                ref={mergeRefs(comboboxRef, ref)}
                                className={className}
                                disabled={disabled}
                                readOnly={readOnly}
                                onChange={(event) => {
                                    comboboxOnChange?.(event);
                                    onChange?.(event);
                                }}
                                onBlur={(event) => {
                                    comboboxOnBlur?.(event);
                                    inputOnBlur?.(event);
                                }}
                                onFocus={(event) => {
                                    comboboxOnFocus?.(event);
                                    inputOnFocus?.(event);
                                }}
                                onKeyDown={(event) => {
                                    comboboxOnKeyDown?.(event);
                                    inputOnKeyDown?.(event);

                                    if (event.defaultPrevented) return;
                                    if (event.key !== 'Enter') return;
                                    if (!isOpen || !activeValue) return;

                                    event.preventDefault();
                                    combobox.setSelectedValue(activeValue);
                                    combobox.setValue(activeValue);
                                    combobox.hide();
                                }}
                            />
                        );
                    }}
                />

                {shouldShowPopover && (
                    <ComboboxPopover
                        store={combobox}
                        portal={false}
                        gutter={4}
                        sameWidth
                        className={classNames(
                            SUGGESTIONS_POPOVER_BASE_CLASSNAME,
                            'overflow-hidden'
                        )}
                    >
                        <SuggestionsList
                            isLoading={isLoading}
                            showEmptyState={showEmptyState}
                            emptyState={emptyState}
                        >
                            {suggestions.map((suggestion) => (
                                <ComboboxItem
                                    key={suggestion.id}
                                    value={suggestion.value}
                                    className={classNames(
                                        SUGGESTIONS_ITEM_BASE_CLASSNAME,
                                        'flex-col'
                                    )}
                                >
                                    <span className="text-neutral-900">{suggestion.label}</span>
                                    {suggestion.secondaryLabel && (
                                        <span className="text-xs text-neutral-500">
                                            {suggestion.secondaryLabel}
                                        </span>
                                    )}
                                </ComboboxItem>
                            ))}
                        </SuggestionsList>
                    </ComboboxPopover>
                )}
            </div>
        );
    }
);

AutocompleteInput.displayName = 'AutocompleteInput';

export default AutocompleteInput;
